import React, { useState, useEffect } from 'react';
import { Users, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, List, Eye, Edit, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Button
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/appSettings';

const Employees = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    loadEmployees(activeSearchKeyword);
  }, [currentPage, activeSearchKeyword, pageSize]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmployees = async (keyword = '') => {
    try {
      setLoading(true);
      setError('');
      
      // If "All" is selected, first get total count, then fetch all records
      if (pageSize === 'all') {
        // First request to get total count
        const countRequestData = {
          Keyword: keyword || '',
          Skip: 0,
          Take: 1
        };
        const countResponse = await MainService.EmployeesInList(countRequestData);
        const total = countResponse?.Total || 0;
        
        if (total > 0) {
          // Now fetch all records
          const allRequestData = {
            Keyword: keyword || '',
            Skip: 0,
            Take: total
          };
          const allResponse = await MainService.EmployeesInList(allRequestData);
          
          if (allResponse && allResponse.Data) {
            setEmployees(allResponse.Data || []);
            setTotalCount(total);
          } else {
            setEmployees([]);
            setTotalCount(0);
          }
        } else {
          setEmployees([]);
          setTotalCount(0);
        }
      } else {
        const skip = (currentPage - 1) * pageSize;
        const requestData = {
          Keyword: keyword || '',
          Skip: skip,
          Take: pageSize
        };

        const response = await MainService.EmployeesInList(requestData);
        
        if (response && response.Data) {
          setEmployees(response.Data || []);
          setTotalCount(response.Total || 0);
        } else {
          setEmployees([]);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
      setEmployees([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveSearchKeyword('');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const exportToExcel = () => {
    if (employees.length === 0) {
      setError('No employees to export');
      return;
    }

    try {
      const exportData = employees.map((emp, index) => ({
        '#': pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1,
        'Employee ID': emp.EmployeeId || 'N/A',
        'Name': emp.Name || 'N/A',
        'Mobile No1': emp.MobileNo1 || 'N/A',
        'Mobile No2': emp.MobileNo2 || 'N/A',
        'Email': emp.EmailId || 'N/A',
        'Designation': emp.Designation?.Name || 'N/A',
        'Emirate': emp.Emirate?.Name || 'N/A',
        'Vehicle': emp.Vehicle?.Number || 'N/A',
        'Reporting Manager': emp.ReportingManager?.Name || 'N/A',
        'Status': emp.IsActive ? 'Active' : 'Inactive'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Employees');
      const fileName = `employees_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleContextMenu = (event, employee) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedEmployee(null);
  };

  const handleView = (employee) => {
    handleMenuClose();
    // TODO: Implement view functionality
    console.log('View employee:', employee);
  };

  const handleEdit = (employee) => {
    handleMenuClose();
    navigate(`/home/accounts/addEmployee/${employee.Id}`);
  };

  const handleDelete = (employee) => {
    handleMenuClose();
    // TODO: Implement delete functionality
    console.log('Delete employee:', employee);
  };

  const sortEmployees = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'EmployeeId':
          aValue = a.EmployeeId || '';
          bValue = b.EmployeeId || '';
          break;
        case 'Name':
          aValue = a.Name || '';
          bValue = b.Name || '';
          break;
        case 'MobileNo1':
          aValue = a.MobileNo1 || '';
          bValue = b.MobileNo1 || '';
          break;
        case 'MobileNo2':
          aValue = a.MobileNo2 || '';
          bValue = b.MobileNo2 || '';
          break;
        case 'EmailId':
          aValue = a.EmailId || '';
          bValue = b.EmailId || '';
          break;
        case 'Designation':
          aValue = a.Designation?.Name || '';
          bValue = b.Designation?.Name || '';
          break;
        case 'Emirate':
          aValue = a.Emirate?.Name || '';
          bValue = b.Emirate?.Name || '';
          break;
        case 'Vehicle':
          aValue = a.Vehicle?.Number || '';
          bValue = b.Vehicle?.Number || '';
          break;
        case 'ReportingManager':
          aValue = a.ReportingManager?.Name || '';
          bValue = b.ReportingManager?.Name || '';
          break;
        case 'Status':
          aValue = a.IsActive ? 1 : 0;
          bValue = b.IsActive ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedEmployees = sortEmployees(employees);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUp className="w-3 h-3 opacity-30" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3" style={{ color: '#46955A' }} />
      : <ArrowDown className="w-3 h-3" style={{ color: '#46955A' }} />;
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
     

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center mb-2">
              <Users className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
            </div>
            <Button
              variant="contained"
              startIcon={<Users className="w-4 h-4" />}
              onClick={() => navigate('/home/accounts/addEmployee')}
              sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
            >
              Add Employee
            </Button>
          </div>

          {/* Search and Actions */}
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <TextField
                fullWidth
                placeholder="Search by name or mobile number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-5 h-5 text-gray-400" />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                      borderBottom: '1px solid #E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    }
                  }
                }}
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={employees.length === 0}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <Download className="w-5 h-5" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            height: '500px',
            position: 'relative',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            '& .MuiPaper-root': {
              boxShadow: 'none'
            }
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={32} sx={{ color: '#46955A' }} />
                <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
              </Box>
            </Box>
          )}
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '5%', padding: '8px' }}>#</TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('EmployeeId')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Employee ID
                    <SortIcon field="EmployeeId" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Name
                    <SortIcon field="Name" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('MobileNo1')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Mobile No1
                    <SortIcon field="MobileNo1" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('MobileNo2')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Mobile No2
                    <SortIcon field="MobileNo2" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('EmailId')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Email
                    <SortIcon field="EmailId" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Designation')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Designation
                    <SortIcon field="Designation" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Emirate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Emirate
                    <SortIcon field="Emirate" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Vehicle')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Vehicle
                    <SortIcon field="Vehicle" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('ReportingManager')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Reporting Manager
                    <SortIcon field="ReportingManager" />
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '8%', padding: '8px' }}>Photo</TableCell>
                <TableCell 
                  align="center" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '8%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Status')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    Status
                    <SortIcon field="Status" />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEmployees.length > 0 ? (
                sortedEmployees.map((employee, index) => (
                  <TableRow
                    key={employee.Id || index}
                    hover
                    onContextMenu={(e) => handleContextMenu(e, employee)}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'context-menu'
                    }}
                  >
                    <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '8px' }}>
                      {pageSize === 'all' 
                        ? index + 1 
                        : (currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {employee.EmployeeId || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {employee.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.MobileNo1 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.MobileNo2 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.EmailId || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.Designation?.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.Emirate?.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.Vehicle?.Number || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {employee.ReportingManager?.Name || 'N/A'}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#F3F4F6',
                          border: '1px solid #E5E7EB'
                        }}
                      >
                        {employee.Photo ? (
                          <img 
                            src={
                              employee.Photo && employee.Photo.startsWith('http') 
                                ? employee.Photo 
                                : `${getApiBaseUrl()}${employee.Photo?.startsWith('/') ? employee.Photo.slice(1) : employee.Photo}`
                            } 
                            alt={employee.Name || 'Employee'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNGM0Y0RjYiLz4KPHBhdGggZD0iTTIwIDEyQzE3LjI0IDEyIDE1IDE0LjI0IDE1IDE3QzE1IDE5Ljc2IDE3LjI0IDIyIDIwIDIyQzIyLjc2IDIyIDI1IDE5Ljc2IDI1IDE3QzI1IDE0LjI0IDIyLjc2IDEyIDIwIDEyWk0yMCAyNEMxNS41OCAyNCAxMiAyNy41OCAxMiAzMkgxNkMxNiAyOS43OSAxNy43OSAyOCAyMCAyOEMyMi4yMSAyOCAyNCAyOS43OSAyNCAzMkgyOEMyOCAyNy41OCAyNC40MiAyNCAyMCAyNFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                            }}
                          />
                        ) : (
                          <Users className="w-5 h-5 text-gray-400" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: employee.IsActive ? '#D1FAE5' : '#FEE2E2',
                          color: employee.IsActive ? '#065F46' : '#991B1B'
                        }}
                      >
                        {employee.IsActive ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Users className="w-8 h-8 text-gray-400" />
                      <Box sx={{ color: '#6B7280' }}>
                        {searchTerm ? `No employees found for "${searchTerm}"` : 'No employees available'}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5
            }
          }}
        >
          <MenuItem
            onClick={() => handleView(selectedEmployee)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>View</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleEdit(selectedEmployee)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>Edit</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedEmployee)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              color: '#DC2626',
              '&:hover': { backgroundColor: '#FEE2E2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </Box>
          </MenuItem>
        </Menu>

        {/* Footer with Pagination and Row Selection */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          gap: 2
        }}>
          {/* Count Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {totalCount > 0 
                ? pageSize === 'all'
                  ? `1-${totalCount} out of ${totalCount}`
                  : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} out of ${totalCount}`
                : '0 out of 0'
              }
            </span>
            
            {/* Rows per page selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Rows per page:</span>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#6B7280'
                },
                '& .Mui-selected': {
                  backgroundColor: '#46955A !important',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#3d7d4f !important'
                  }
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#f0f9f4'
                }
              }}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default Employees;

